import { Component } from '@theme/component';
import { getTruck, setTruck, onTruckChange } from '@theme/dialed-truck';
import { YmmPicker, truckLabel } from '@theme/dialed-ymm';

/**
 * <dialed-bed-layouts data-mode="pick|tabs">: shows one truck's layout panel at a time
 * (sections/dialed-bed-layouts.liquid).
 * - pick: Year → Make → Model → Bed selects, starting on the saved truck. Picking saves it.
 * - tabs: one button per bed on a truck model page.
 */
/* The printable install guide: one letter or A4 page */
const GUIDE_CSS = `
  @page { margin: 12mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font: 13px/1.45 system-ui, -apple-system, 'Segoe UI', sans-serif; color: #232320; }
  header { display: flex; flex-direction: column; gap: 4px; padding-bottom: 10px; border-bottom: 3px solid #232320; }
  .eyebrow { margin: 0; font-size: 11px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: #ff5a0a; }
  h1 { margin: 0; font-size: 28px; line-height: 1; text-transform: uppercase; }
  .sub { margin: 0; color: #5c5b55; }
  img { display: block; width: 100%; max-height: 120mm; margin: 12px 0; object-fit: contain; }
  .cols { display: grid; grid-template-columns: 1.4fr 1fr; gap: 24px; }
  h2 { margin: 0 0 8px; font-size: 13px; letter-spacing: .1em; text-transform: uppercase; }
  ol { margin: 0; padding-left: 20px; }
  li { margin-bottom: 6px; }
  li::marker { font-weight: 700; color: #ff5a0a; }
  dl { margin: 0; }
  .dl-parts__row { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; padding: 5px 0; border-bottom: 1px solid #d8d4c7; }
  dt { display: flex; flex-direction: column; font-weight: 600; }
  .dl-parts__size { font-weight: 400; font-size: 11px; color: #5c5b55; }
  dd { margin: 0; font-size: 18px; font-weight: 700; }
  footer { margin-top: 14px; padding-top: 8px; border-top: 1px solid #d8d4c7; font-size: 11px; color: #5c5b55; }
`;

class DialedBedLayouts extends Component {
  /** @type {Array<{handle:string,make:string,model:string,years:string,bed:string,label:string}>} */
  fits = [];
  /** @type {YmmPicker | null} */
  picker = null;
  #abort = new AbortController();
  #unsubscribe = () => {};

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });

    if (this.dataset.mode !== 'pick') return;
    try {
      this.fits = JSON.parse(this.querySelector('[data-dialed-trucks]')?.textContent ?? '[]');
    } catch {
      return;
    }
    // Only trucks that have a layout panel
    const panels = new Set([...this.querySelectorAll('[data-layout]')].map((p) => /** @type {HTMLElement} */ (p).dataset.layout));
    this.fits = this.fits.filter((fit) => panels.has(fit.handle));

    const { year, make, model, config } = /** @type {Record<string, HTMLSelectElement>} */ (this.refs);
    if (!year || !make || !model || !config) return;
    this.picker = new YmmPicker(this.fits, { year, make, model, bed: config });
    this.picker.bind(() => this.#picked(true), signal);

    this.#unsubscribe = onTruckChange((truck) => {
      if (!truck || !this.picker) return;
      if (truck.fit !== this.dataset.fit || (truck.year && truck.year !== this.picker.state.year)) {
        this.picker.set({ year: truck.year, make: truck.make, model: truck.model, fit: truck.fit });
        this.#picked(false);
      }
    });
    if (!getTruck()) {
      this.picker.set({});
      this.#picked(false);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#unsubscribe();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const tab = target?.closest('[data-tab]');
    if (tab instanceof HTMLElement) {
      for (const other of this.querySelectorAll('[data-tab]')) other.setAttribute('aria-pressed', String(other === tab));
      this.#show(tab.dataset.tab ?? '');
    }
    const print = target?.closest('[data-print]');
    if (print) this.#print(print.closest('[data-layout]'));
    // "Shop this kit" remembers the truck for the kit page and the rest of the visit
    const cta = target?.closest('a[data-fit]');
    if (cta instanceof HTMLElement) {
      const fit = this.fits.find((f) => f.handle === cta.dataset.fit);
      const year = this.picker?.fit()?.handle === fit?.handle ? this.picker?.state.year : '';
      if (fit) setTruck({ year: year || undefined, make: fit.make, model: fit.model, fit: fit.handle, label: truckLabel(fit, year) });
    }
  };

  /**
   * Prints the panel's guide (its <template data-guide>: layout, steps and parts) from a window of
   * its own, so the page's header, footer and other sections stay off the paper.
   * @param {Element | null} panel
   */
  #print(panel) {
    const guide = panel?.querySelector('template[data-guide]');
    if (!(guide instanceof HTMLTemplateElement)) return;
    const win = window.open('', '_blank', 'width=900,height=1100');
    if (!win) return;
    const title = guide.content.querySelector('h1')?.textContent ?? '';
    win.document.write(`<!doctype html><html lang="${document.documentElement.lang}"><head><meta charset="utf-8"><base href="${location.href}"><title>${title}</title><style>${GUIDE_CSS}</style></head><body>${guide.innerHTML}</body></html>`);
    win.document.close();
    const img = win.document.querySelector('img');
    const go = () => {
      win.focus();
      win.print();
    };
    if (img && !img.complete) img.addEventListener('load', go, { once: true });
    else go();
  }

  /** @param {boolean} save */
  #picked(save) {
    const fit = this.picker?.fit() ?? null;
    const year = this.picker?.state.year ?? '';
    this.#show(fit?.handle ?? '');
    if (save && fit) setTruck({ year, make: fit.make, model: fit.model, fit: fit.handle, label: truckLabel(fit, year) });
  }

  /** @param {string} handle */
  #show(handle) {
    this.dataset.fit = handle;
    for (const panel of this.querySelectorAll('[data-layout]')) {
      const el = /** @type {HTMLElement} */ (panel);
      el.hidden = el.dataset.layout !== handle;
      // Panels start hidden, so their lazy images may never be picked up: load the shown one now
      if (!el.hidden) el.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    }
    const empty = this.querySelector('[data-empty]');
    if (empty instanceof HTMLElement) empty.hidden = Boolean(handle);
  }
}

if (!customElements.get('dialed-bed-layouts')) customElements.define('dialed-bed-layouts', DialedBedLayouts);
